import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { MenuItem } from "@blueprintjs/core";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-final-form";
import SearchSelectInput from "rs-components/Input/components/SearchSelectInput";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import Input from "rs-components/Input";
import NumberInput from "rs-components/Input/components/NumberInput";
import TextTruncate from "components/TextTruncate";
import HiddenInput from "rs-components/Input/components/HiddenInput";
import SimpleRemoveButton from "components/SimpleRemoveButton";
import Button, { ButtonSize, ButtonStyle } from "components/Button";
import RangeValue from "rs-components/RangeValue";
import { SimulationFormFields, VariantSourceType } from "../../../..";
import { checkRecipeParameterIsCompleted } from "store/recipe/recipe.utils";
import { formatCellModelParameterValue } from "store/cell-model/cell-model.utils";
import { NoUnitsType } from "store/recipe/recipe.types";
import { DynamicType } from "store/cell-model/cell-model.types";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { setDeleteTarget } from "store/knowledge-base-item/knowledge-base-item.actions";
import { RemovableEntityType } from "store/knowledge-base-item/knowledge-base-item.types";
import { isNotEmpty } from "utils/validate";
import { getNumberFromNumberString } from "utils/string";
import { getFieldRestrictions } from "store/knowledge-base-item/knowledge-base-item.utils";
import { cellModelParameterUnitsType, findDictionaryItemByValue, getDefaultUnitsPrefix, } from "shared/rsDictionary";
import { DIALOG_ACTION_TITLE, VariantLabels } from "shared/labels";
import * as styles from "./styles.module.scss";
import * as multiSelectStyles from "components/Controls/MultiSelect/styles.module.scss";
const dynamicTypesOptions = [
    { value: DynamicType.FIXED, label: "None" },
    { value: DynamicType.RANGE, label: "Within Range" },
    { value: DynamicType.DISTRIBUTION, label: "Has an optimum" },
];
const Parameter = ({ name, renderProps, index, target, targetType, formParameters, fieldArrayName, restrictions, incomplete, onVariantConfigurationFieldChange, }) => {
    const { fields } = renderProps;
    const form = useForm();
    const dispatch = useDispatch();
    const editable = useSelector(isEditableSelector);
    const isCellModelParameter = useMemo(() => targetType === VariantSourceType.CellModel, [targetType]);
    const usedParameterIds = useMemo(() => fields.value.map((item) => (isCellModelParameter ? `${item?.parameter?.parameterId}` : `${item?.id}`)), [fields.value, isCellModelParameter]);
    const cellModelParametersList = useMemo(() => {
        if (targetType === VariantSourceType.Recipe)
            return [];
        return target?.state?.adjustmentParameters;
    }, [target, targetType]);
    const cellModelParametersOptions = useMemo(() => {
        if (targetType === VariantSourceType.Recipe)
            return [];
        return (cellModelParametersList ?? [])
            .filter(({ id }) => isNotEmpty(id) && !usedParameterIds.includes(id ?? ""))
            .map(({ parameterName, id }) => ({ value: id ?? "", label: parameterName ?? "" }));
    }, [cellModelParametersList, usedParameterIds, targetType]);
    const getRecipeParametersToOptimizeList = useCallback((parameters) => parameters
        .filter((parameter) => checkRecipeParameterIsCompleted(parameter) && !usedParameterIds.includes(`${parameter.id}`))
        ?.map(({ id, name }) => ({
        value: id,
        label: name,
    })), [usedParameterIds]);
    const parameterItems = useMemo(() => {
        if (!isNotEmpty(target?.state?.parameters)) {
            return [];
        }
        return targetType === VariantSourceType.Recipe
            ? getRecipeParametersToOptimizeList(target?.state?.parameters)
            : cellModelParametersOptions;
    }, [getRecipeParametersToOptimizeList, cellModelParametersOptions, target?.state, targetType]);
    const parameter = useMemo(() => (isCellModelParameter ? fields.value?.[index]?.parameter : fields.value?.[index]), [fields.value, index, isCellModelParameter]);
    const selectedCellModelParameter = useMemo(() => cellModelParametersList?.find(({ id }) => id === parameter?.[SimulationFormFields._selectedParameterId]), [cellModelParametersList, parameter]);
    const selectedParameter = useMemo(() => {
        if (!isNotEmpty(target?.state?.parameters)) {
            return {};
        }
        if (targetType === VariantSourceType.Recipe) {
            const recipeParameter = target?.state?.parameters?.find(({ id }) => `${id}` === `${parameter?.[SimulationFormFields._selectedParameterId]}`);
            return { ...recipeParameter, value: getNumberFromNumberString(recipeParameter?.value ?? "") };
        }
        else {
            return {
                id: parameter?.[SimulationFormFields._selectedParameterId],
                name: selectedCellModelParameter?.parameterName,
                value: formatCellModelParameterValue(selectedCellModelParameter?.parameterValue),
                units: selectedCellModelParameter?.units,
            };
        }
    }, [parameter, target?.state, targetType, selectedCellModelParameter]);
    const getParameterDictionaryUnits = useCallback((withPrefix) => findDictionaryItemByValue(cellModelParameterUnitsType, selectedParameter?.units, withPrefix ? getDefaultUnitsPrefix : undefined), [selectedParameter?.units]);
    const valueAppearance = useMemo(() => {
        if (incomplete) {
            return "Undefined";
        }
        return `${selectedParameter?.value} ${selectedParameter?.units === NoUnitsType.NoUnits ? "" : getParameterDictionaryUnits(true)?.label || ""}`;
    }, [getParameterDictionaryUnits, selectedParameter?.units, selectedParameter?.value, incomplete]);
    const unitsAppearance = useMemo(() => getParameterDictionaryUnits()?.value === NoUnitsType.NoUnits
        ? null
        : getParameterDictionaryUnits()?.label, [getParameterDictionaryUnits]);
    const isParameterSelected = useMemo(() => isNotEmpty(parameter[SimulationFormFields._selectedParameterId]), [parameter]);
    const setParameterDeleteTarget = useCallback(() => {
        dispatch(setDeleteTarget({
            id: index,
            type: RemovableEntityType.SIMULATION_VARIANT_PARAMETER,
        }));
    }, [dispatch, index]);
    const onConfirmAddingParameter = useCallback(() => {
        fields.update(index, isCellModelParameter
            ? {
                parameter: {
                    ...parameter,
                    [SimulationFormFields.type]: selectedCellModelParameter?.uncertainty?.type,
                    [SimulationFormFields.parameterId]: parameter[SimulationFormFields._selectedParameterId],
                    notSaved: false,
                },
            }
            : { ...parameter, id: parameter[SimulationFormFields._selectedParameterId], notSaved: false });
    }, [index, fields, isCellModelParameter, parameter, selectedCellModelParameter?.uncertainty?.type]);
    const onConfigurationFieldChange = useCallback((fieldName, fieldValue) => {
        if (isNaN(Number(fieldValue))) {
            return;
        }
        const updatedVariantFields = isCellModelParameter
            ? {
                [SimulationFormFields.cellModelParameters]: [
                    ...formParameters?.filter((item) => item?.parameter.parameterId !== parameter.parameterId),
                    { [SimulationFormFields.parameter]: { ...parameter, [fieldName]: fieldValue } },
                ],
            }
            : {
                [SimulationFormFields.recipeParameters]: [
                    ...formParameters?.filter(({ id }) => id !== parameter.id),
                    { ...parameter, [fieldName]: fieldValue },
                ],
            };
        onVariantConfigurationFieldChange(updatedVariantFields);
    }, [isCellModelParameter, formParameters, parameter, onVariantConfigurationFieldChange]);
    const renderInputsBasedOnType = useMemo(() => {
        const cellModelUncertaintyType = parameter?.type;
        const isFixedUncertainty = cellModelUncertaintyType === DynamicType.FIXED;
        const isStaticParameter = !!selectedCellModelParameter && !selectedCellModelParameter?.isDynamic;
        if ([targetType === VariantSourceType.Recipe, isFixedUncertainty, isStaticParameter].some(Boolean)) {
            return (React.createElement(Input, { name: isCellModelParameter
                    ? `${name}.${SimulationFormFields.parameter}.${SimulationFormFields.value}`
                    : `${name}.${SimulationFormFields.value}`, component: NumberInput, label: "New Value", readonly: !editable, classes: {
                    container: styles.field,
                    label: styles.label,
                }, restrictions: {
                    IsNumber: true,
                    ...(isCellModelParameter
                        ? getFieldRestrictions(restrictions, ["Parameter", "Fixed", "Value"])
                        : getFieldRestrictions(restrictions, ["Value"])),
                }, placeholder: unitsAppearance, onBlur: ({ value }) => onConfigurationFieldChange(SimulationFormFields.value, value) }));
        }
        switch (cellModelUncertaintyType) {
            case DynamicType.DISTRIBUTION:
                return (React.createElement("div", { className: styles.row },
                    React.createElement(Input, { name: `${name}.${SimulationFormFields.parameter}.${SimulationFormFields.mean}`, component: NumberInput, label: "Max rate" // TODO: different labels for effect and dynamic (?)
                        , readonly: !editable, classes: {
                            container: styles.field,
                            label: styles.label,
                        }, restrictions: {
                            IsNumber: true,
                            ...getFieldRestrictions(restrictions, ["Parameter", "Distribution", "Mean"]),
                        }, placeholder: unitsAppearance, onBlur: ({ value }) => onConfigurationFieldChange(SimulationFormFields.mean, value) }),
                    React.createElement("span", { className: styles["long-dash"] }),
                    React.createElement(Input, { name: `${name}.${SimulationFormFields.parameter}.${SimulationFormFields.sd}`, component: NumberInput, label: "Spread", readonly: !editable, classes: {
                            container: styles.field,
                            label: styles.label,
                        }, restrictions: {
                            IsNumber: true,
                            ...getFieldRestrictions(restrictions, ["Parameter", "Distribution", "SD"]),
                        }, onBlur: ({ value }) => onConfigurationFieldChange(SimulationFormFields.sd, value) })));
            case DynamicType.RANGE:
                return (React.createElement(RangeValue, { from: {
                        component: NumberInput,
                        name: `${name}.${SimulationFormFields.parameter}.${SimulationFormFields.from}`,
                        placeholder: unitsAppearance,
                        label: "Min",
                        classes: {
                            container: styles.rangeField,
                            label: styles.label,
                        },
                        readonly: !editable,
                        restrictions: {
                            IsNumber: true,
                            ...getFieldRestrictions(restrictions, ["Parameter", "Range", "From"]),
                        },
                        onBlur: ({ value }) => onConfigurationFieldChange(SimulationFormFields.from, String(value)),
                    }, to: {
                        component: NumberInput,
                        name: `${name}.${SimulationFormFields.parameter}.${SimulationFormFields.to}`,
                        placeholder: unitsAppearance,
                        label: "Max",
                        classes: {
                            container: styles.rangeField,
                            label: styles.label,
                        },
                        readonly: !editable,
                        restrictions: {
                            IsNumber: true,
                            ...getFieldRestrictions(restrictions, ["Parameter", "Range", "To"]),
                        },
                        onBlur: ({ value }) => onConfigurationFieldChange(SimulationFormFields.to, String(value)),
                    }, editable: editable }));
        }
    }, [
        targetType,
        editable,
        name,
        parameter?.type,
        unitsAppearance,
        isCellModelParameter,
        selectedCellModelParameter,
        restrictions,
        onConfigurationFieldChange,
    ]);
    const noSearchResultsMessage = useMemo(() => {
        if (!parameterItems?.length)
            return VariantLabels.NO_PARAMETERS;
        return isCellModelParameter
            ? VariantLabels.NO_MATCHING_CELL_MODEL_PARAMETERS
            : VariantLabels.NO_MATCHING_RECIPE_PARAMETERS;
    }, [isCellModelParameter, parameterItems]);
    const onRemoveParameter = useCallback((isConfirmed) => {
        if (!isConfirmed) {
            return;
        }
        const updatedParameters = formParameters.filter((_, i) => i !== index);
        const updatedVariantFields = isCellModelParameter
            ? { [SimulationFormFields.cellModelParameters]: updatedParameters }
            : { [SimulationFormFields.recipeParameters]: updatedParameters };
        onVariantConfigurationFieldChange(updatedVariantFields);
        form.change(fieldArrayName, updatedParameters);
    }, [form, fieldArrayName, formParameters, index, isCellModelParameter, onVariantConfigurationFieldChange]);
    return (React.createElement("div", { key: `${name}-${index}-${fields.length}`, className: styles.parameter },
        React.createElement(Input, { name: isCellModelParameter
                ? `${name}.${SimulationFormFields.parameter}.${SimulationFormFields._selectedParameterId}`
                : `${name}.${SimulationFormFields._selectedParameterId}`, component: SearchSelectInput, items: parameterItems, readonly: !parameter.notSaved, value: { value: selectedParameter?.id, label: selectedParameter?.name }, classes: {
                component: classNames(styles["parameter-name"], {
                    [styles["select-viewMode"]]: !parameter.notSaved,
                }),
            }, noResultsRenderer: React.createElement(MenuItem, { className: multiSelectStyles.emptyList, disabled: true, text: noSearchResultsMessage }), errorLabel: "Parameter", restrictions: isCellModelParameter ? { IsMandatory: true } : getFieldRestrictions(restrictions, ["Id"]) }),
        React.createElement(Input, { name: isCellModelParameter
                ? `${name}.${SimulationFormFields.parameter}.${SimulationFormFields.parameterId}`
                : `${name}.${SimulationFormFields.id}`, component: HiddenInput, restrictions: isCellModelParameter ? { IsMandatory: true } : getFieldRestrictions(restrictions, ["Id"]) }),
        isParameterSelected && (React.createElement(React.Fragment, null,
            React.createElement(TextTruncate, { className: classNames(styles["parameter-value"], {
                    [styles["parameter-value-viewMode"]]: !parameter.notSaved,
                    [styles.incomplete]: incomplete,
                }), text: valueAppearance }),
            !parameter.notSaved && (React.createElement(React.Fragment, null,
                isCellModelParameter && (React.createElement(Input, { name: `${name}.${SimulationFormFields.parameter}.${SimulationFormFields.type}`, component: SelectInput, items: dynamicTypesOptions, label: "Uncertainty", itemRenderer: SelectItemRenderer, valueFormatter: ({ value }) => value, classes: { component: styles.select, label: styles.label }, readonly: !editable, restrictions: getFieldRestrictions(restrictions, ["Parameter", "Fixed", "Type"]) })),
                renderInputsBasedOnType,
                incomplete && (React.createElement(TextTruncate, { className: styles.errorMessage, text: VariantLabels.PARAMETER_NOT_VALID })),
                editable && (React.createElement(SimpleRemoveButton, { className: styles["parameter-deleteIcon"], onOpen: setParameterDeleteTarget, action: onRemoveParameter, isMenuItem: false, withConfirmation: isNotEmpty(parameter?.value) })))))),
        !!parameter.notSaved && (React.createElement("div", { className: styles["parameter-buttons"] },
            React.createElement(Button, { onClick: () => fields.pop(), classes: { root: styles["parameter-buttons-button"] }, label: DIALOG_ACTION_TITLE.CANCEL, role: "button", style: ButtonStyle.Secondary, size: ButtonSize.Normal, showTooltipIfDisabled: true }),
            React.createElement(Button, { classes: { root: styles["parameter-buttons-button"] }, label: DIALOG_ACTION_TITLE.OK, role: "button", onClick: onConfirmAddingParameter, style: ButtonStyle.Primary, size: ButtonSize.Normal, disabled: !parameter[SimulationFormFields._selectedParameterId], showTooltipIfDisabled: true })))));
};
export default Parameter;
